import { Link } from "@tanstack/react-router";
import { Phone, CalendarCheck, ShieldCheck } from "lucide-react";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture } from "@/components/site/images";
import { ContactForm } from "@/components/site/ContactForm";
import { CASE_STUDIES } from "@/components/site/case-studies";
import type { CaseStudyPhoto, ProjectCaseStudy } from "@/components/site/case-studies";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";

export function CaseStudyHero({ study }: { study: ProjectCaseStudy }) {
  return (
    <section className="storm-bg text-brand-foreground relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_10%,white,transparent_40%)]" />
      <div className="relative mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div className="text-sm font-semibold uppercase tracking-wider text-cta mb-3">
          {study.city}, {study.state} · {study.roofType}
        </div>
        <h1 className="text-3xl md:text-5xl font-display font-bold max-w-4xl">{study.h1}</h1>
        <p className="mt-4 text-base md:text-lg opacity-90 max-w-3xl">{study.summary}</p>
        {study.trustPoints.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm opacity-90">
            {study.trustPoints.map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5">
                <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
                {t}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <a href="#request-inspection" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> {study.heroCta ?? "Request a Free Metal-Roof Inspection"}
          </a>
          <a href={BUSINESS.phoneHref} className="btn-outline-light w-full sm:w-auto">
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

export function FactCard({ study, heading }: { study: ProjectCaseStudy; heading: string }) {
  if (study.facts.length === 0) return null;
  return (
    <section className="not-prose my-8">
      <h2 className="font-display text-2xl font-bold mb-4">{heading}</h2>
      <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {study.facts.map((f) => (
          <div key={f.label} className="border border-border rounded-lg p-4 bg-card">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</dt>
            <dd className="mt-1 font-semibold">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function PhotoFigure({ photo }: { photo: CaseStudyPhoto }) {
  return (
    <figure className="not-prose">
      <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
        {photo.label && (
          <span className="absolute left-3 top-3 z-10 rounded bg-brand/90 px-2.5 py-1 text-xs font-display font-bold uppercase tracking-wider text-brand-foreground">
            {photo.label}
          </span>
        )}
        <ResponsivePicture
          image={photo.image}
          alt={photo.alt}
          sizes="(min-width: 1024px) 45vw, 100vw"
          imgClassName="w-full h-auto"
        />
      </div>
      <figcaption className="mt-2 text-sm text-muted-foreground">{photo.caption}</figcaption>
    </figure>
  );
}

export function BeforeAfterSection({ study, heading }: { study: ProjectCaseStudy; heading: string }) {
  const photos = [...(study.before ?? []), ...(study.during ?? []), ...(study.after ?? [])];
  if (photos.length === 0) return null;
  return (
    <section className="my-8">
      <h2 className="font-display text-2xl font-bold mb-4">{heading}</h2>
      <div className="grid gap-6 md:grid-cols-2">
        {photos.map((p) => (
          <PhotoFigure key={p.alt} photo={p} />
        ))}
      </div>
    </section>
  );
}

export function RelatedServices({ study, heading }: { study: ProjectCaseStudy; heading: string }) {
  const links = [...study.relatedServices];
  if (study.relatedCityPage) links.push(study.relatedCityPage);
  if (links.length === 0) return null;
  return (
    <section className="not-prose my-8">
      <h2 className="font-display text-2xl font-bold mb-4">{heading}</h2>
      <ul className="grid sm:grid-cols-2 gap-4">
        {links.map((l) => (
          <li key={l.to} className="border border-border rounded-lg p-4 bg-card">
            <Link to={l.to} className="font-semibold text-brand hover:underline">
              {l.label}
            </Link>
            {l.description && <p className="mt-1 text-sm text-muted-foreground">{l.description}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function FaqSection({ study, heading }: { study: ProjectCaseStudy; heading: string }) {
  if (study.faqs.length === 0) return null;
  return (
    <section className="my-8">
      <h2 className="font-display text-2xl font-bold mb-4">{heading}</h2>
      <div className="not-prose space-y-3">
        {study.faqs.map((f) => (
          <details key={f.q} className="border border-border rounded-lg bg-card">
            <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
            <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

export function CaseStudyForm({ study }: { study: ProjectCaseStudy }) {
  return (
    <section id="request-inspection" className="not-prose my-10 scroll-mt-24">
      <h2 className="font-display text-2xl font-bold mb-2">{study.formHeading}</h2>
      <p className="text-sm text-muted-foreground mb-4 max-w-2xl">{study.formCopy}</p>
      <div className="max-w-2xl">
        <ContactForm defaultService={study.formService} formName={study.formName} />
      </div>
    </section>
  );
}

export function RelatedProjects({ study, heading }: { study: ProjectCaseStudy; heading: string }) {
  if (study.relatedProjects.length === 0) return null;
  return (
    <section className="not-prose my-8">
      <h2 className="font-display text-2xl font-bold mb-4">{heading}</h2>
      <div className="grid gap-5 md:grid-cols-3">
        {study.relatedProjects.map((p) => (
          <Link key={p.alt} to={p.to} className="group block">
            <div className="overflow-hidden rounded-lg border border-border bg-muted">
              <ResponsivePicture
                image={p.image}
                alt={p.alt}
                sizes="(min-width: 768px) 33vw, 100vw"
                imgClassName="w-full h-56 object-cover"
              />
            </div>
            <div className="mt-2">
              <span className="font-semibold group-hover:text-brand">{p.title}</span>
              {p.note && <span className="block text-sm text-cta font-semibold">{p.note}</span>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function CaseStudyFinalCta({ study }: { study: ProjectCaseStudy }) {
  return (
    <section className="not-prose my-10">
      <h2 className="font-display text-2xl font-bold mb-2">{study.finalCta.heading}</h2>
      <p className="text-muted-foreground max-w-2xl">{study.finalCta.copy}</p>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <a href="#request-inspection" className="btn-cta w-full sm:w-auto">
          <CalendarCheck className="size-5" /> Schedule My Free Inspection
        </a>
        <a
          href={BUSINESS.phoneHref}
          className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors"
        >
          <Phone className="size-5" /> Call {BUSINESS.phone}
        </a>
      </div>
    </section>
  );
}

export function projectCrumbs(study: ProjectCaseStudy) {
  return [
    { label: "Roofing Projects", href: "/projects" },
    { label: study.breadcrumbLabel, href: `/projects/${study.slug}` },
  ];
}

export function projectStudyHead(study: ProjectCaseStudy) {
  const path = `/projects/${study.slug}`;
  return {
    meta: [
      { title: study.title },
      { name: "description", content: study.metaDescription },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: study.title },
      { property: "og:description", content: study.metaDescription },
      { property: "og:url", content: canonical(path) },
      { property: "og:type", content: "article" },
      { property: "og:image", content: study.ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: study.ogImage },
    ],
    links: [{ rel: "canonical", href: canonical(path) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          breadcrumbListJsonLd(projectCrumbs(study)),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: study.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  };
}

export function ProjectStudyPage({ study }: { study: ProjectCaseStudy }) {
  const others = CASE_STUDIES.filter((s) => s.slug !== study.slug);
  const sameType = others.filter((s) => s.roofType.includes("hingle") === study.roofType.includes("hingle"));
  const related = [...sameType, ...others.filter((s) => !sameType.includes(s))].slice(0, 3).map((s) => {
    const cover = (s.after ?? s.during ?? s.before ?? [])[0];
    return { image: cover.image, alt: cover.alt, title: s.breadcrumbLabel, to: `/projects/${s.slug}`, note: s.statusState === "in-progress" ? "In Progress" : undefined };
  });
  const withRelated = { ...study, relatedProjects: related };
  const isShingle = study.roofType.toLowerCase().includes("shingle");
  return (
    <>
      <Breadcrumbs items={projectCrumbs(study)} />
      <CaseStudyHero study={study} />
      <div className="mx-auto max-w-4xl px-4 py-10 md:py-14 prose prose-slate max-w-none lg:prose-lg">
        <FactCard study={study} heading="Project Overview" />
        <BeforeAfterSection study={study} heading={`${study.city} Project Photographs`} />
        {study.sections?.map((sec) => (
          <section key={sec.h2}>
            <h2>{sec.h2}</h2>
            {sec.p.map((t) => <p key={t}>{t}</p>)}
            {sec.bullets && <ul>{sec.bullets.map((b) => <li key={b}>{b}</li>)}</ul>}
          </section>
        ))}
        <h2>Serving {study.city} and Citrus County</h2>
        <p>
          {study.city} is part of Destination Roofing's normal service area. Owner and licensed roofing
          contractor Deston Judy generally performs inspections personally and explains the options in
          plain language. Learn more about our{" "}
          {study.relatedCityPage && <Link to={study.relatedCityPage.to}>roofing services in {study.city}</Link>}
          {study.serviceLink && <>, our approach to <Link to={study.serviceLink.to}>{study.serviceLink.label}</Link></>}
          , and when a <Link to="/roof-replacement">full roof replacement</Link> makes sense versus a{" "}
          <Link to="/roof-repair">roof repair</Link>. {isShingle ? "Compare other finished roofs" : "See more finished work"} in our{" "}
          <Link to="/gallery">project gallery</Link> or the full list of{" "}
          <Link to="/projects">roofing projects</Link>.
        </p>
        <RelatedServices study={study} heading="Related Roofing Services" />
        <FaqSection study={study} heading={`${study.city} Roofing Questions`} />
        <CaseStudyForm study={study} />
        <RelatedProjects study={withRelated} heading="More Destination Roofing Projects" />
        <CaseStudyFinalCta study={study} />
      </div>
    </>
  );
}
